/**
 * Форматирование для дашборда — перенос хелперов макета (dashbdv3.html, §0.7 ui-blocks.md).
 *
 * Даты приходят с бэкенда строками ISO с зоной клиники. Календарные значения
 * (день, месяц, год) берём из самой строки, а не через `Date`, иначе браузер
 * в другой таймзоне сдвинет дату на сутки.
 */

import type { CSSProperties } from 'react';
import type { DeadlineState, Stage } from './types';

/** `2026-09-10T23:59:59.999+03:00` → `10.09.2026`. */
export function fmtDate(iso: string | null | undefined): string {
    if (!iso) return '—';
    const [y, m, d] = iso.slice(0, 10).split('-');
    if (!y || !m || !d) return '—';
    return `${d}.${m}.${y}`;
}

/** `2026-09-10T…` → `10.09`. */
export function fmtShort(iso: string | null | undefined): string {
    if (!iso) return '—';
    const [, m, d] = iso.slice(0, 10).split('-');
    if (!m || !d) return '—';
    return `${d}.${m}`;
}

/** Разница в календарных днях между двумя ISO-строками (по датам, без времени). */
export function daysBetween(a: string, b: string): number {
    const toUtc = (iso: string): number => {
        const parts = iso.slice(0, 10).split('-');
        return Date.UTC(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
    };
    return Math.round((toUtc(b) - toUtc(a)) / 86400000);
}

/** Русская плюрализация: 1 день / 2 дня / 5 дней. */
export function plural(n: number, one: string, few: string, many: string): string {
    const abs = Math.abs(n) % 100;
    const last = abs % 10;
    if (abs > 10 && abs < 20) return many;
    if (last > 1 && last < 5) return few;
    if (last === 1) return one;
    return many;
}

export function dayWord(n: number): string {
    return plural(n, 'день', 'дня', 'дней');
}

export function caseWord(n: number): string {
    return plural(n, 'дело', 'дела', 'дел');
}

export function appealWord(n: number): string {
    return plural(n, 'обращение', 'обращения', 'обращений');
}

/** Округление до 0,1 с запятой как разделителем: `4,2`. */
export function num1(x: number): string {
    return (Math.round(x * 10) / 10).toString().replace('.', ',');
}

export const STAGE_LABELS: Record<Stage, string> = {
    registered: 'Зарегистрировано',
    qualified: 'Квалифицировано',
    investigation: 'Разбирательство',
    draft: 'Проект ответа',
    approval: 'На согласовании',
    sent: 'Ответ направлен',
};

export function stageLabel(stage: Stage): string {
    return STAGE_LABELS[stage] ?? stage;
}

/** Класс окраски даты: в срок — пусто, горит — `soon`, просрочено — `late`. */
export function deadlineClass(state: DeadlineState | null | undefined): string {
    if (state === 'late') return 'late';
    if (state === 'soon') return 'soon';
    return '';
}

/**
 * Сокращение названий законов — `shortLaw()` макета (dashbdv3.html 2360–2368),
 * замены перенесены дословно и в том же порядке.
 */
export function shortLaw(s: string): string {
    return s
        .replace('Об основах охраны здоровья граждан в Российской Федерации', '323-ФЗ')
        .replace('О порядке рассмотрения обращений граждан Российской Федерации', '59-ФЗ')
        .replace('Об адвокатской деятельности и адвокатуре в Российской Федерации', '63-ФЗ')
        .replace('О защите прав потребителей', 'ЗоЗПП')
        .replace('Кодекс профессиональной этики врача Российской Федерации', 'Кодекс этики врача')
        .replace(/,?\s*Статья\s*/i, ' · ст. ');
}

/**
 * Подсветка числа в строке категории. В макете красным горело «ЧП / смерть»,
 * оранжевым — денежное требование; здесь те же роли у настоящих категорий мини.
 */
export function categoryHighlight(category: string): string {
    if (category === 'ЧП / смерть / экстренная помощь') return 'highlight-red';
    if (category === 'Претензия / досудебное требование') return 'highlight-orange';
    return '';
}

/** Ширина полосы в процентах — инлайновый стиль, как в макете. */
export function barWidth(value: number, max: number): string {
    const m = max > 0 ? max : 1;
    return `${Math.round((value / m) * 100)}%`;
}

/* ---------------------------------------------------------------
   Инлайновые стили, которые задаёт сам макет (ui-css.md §5.1).
   Новых классов не заводим: то, что в dashbdv3.html написано в
   атрибуте style, здесь остаётся инлайновым.
   --------------------------------------------------------------- */

/** `<div class="stat-block" style="margin-bottom:18px">`. */
export const BLOCK_GAP: CSSProperties = { marginBottom: 18 };

/** Приписка в заголовке блока: `· 4 дела`, `· 7 дней`. */
export const HEAD_NOTE: CSSProperties = {
    textTransform: 'none',
    fontWeight: 500,
    color: '#56697f',
};

/** Правая приписка заголовка: «ближайшие 14 дней», «за период». */
export const HEAD_NOTE_SM: CSSProperties = {
    textTransform: 'none',
    fontWeight: 500,
    color: '#56697f',
    fontSize: '14px',
};

/** `<div class="block-body" style="padding:14px 20px 16px">` у календаря. */
export const CAL_BODY: CSSProperties = { padding: '14px 20px 16px' };

/** Правая группа в `.section-title-row`. */
export const TITLE_ROW_ACTIONS: CSSProperties = {
    display: 'flex',
    gap: 12,
    alignItems: 'center',
    flexWrap: 'wrap',
};

/** Точка категории в `.stat-row .label` — цвет приходит с бэкенда. */
export function catDotStyle(color: string): CSSProperties {
    return {
        width: 6,
        height: 6,
        borderRadius: '50%',
        background: color,
        display: 'inline-block',
        flex: 'none',
    };
}
